/**
 * Reusable collapsible section card for the Session Detail page.
 * All sections (Session, Customer, Devices, Charges, Terms, Payment, Audit)
 * use the same chrome: a floating white card (sm4rt-web-ui skill) with an
 * uppercase tracked heading, right-side adornment (chip / total), tap-anywhere
 * header, and a smooth collapse. Styling lives in checkInSession.css.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Collapse } from '@mui/material';
import { IconAlertCircle, IconCheck, IconChevronDown } from '@tabler/icons-react';

import { Chip } from '../../chip';
import type { SectionStatus } from './sectionCompletion';

export interface CollapsibleSectionProps {
  title: string;
  /** Right-aligned adornment shown on the header line (chip / total / etc.) */
  adornment?: React.ReactNode;
  /**
   * Whether this step of the check-in is finished (`deriveSectionProgress`).
   * Marks the card and, until the operator takes over, decides whether it
   * opens: an outstanding section is open, a finished one collapses out of the
   * way. Omit it for reference sections (Session, Print, Audit) — they are not
   * steps and carry no mark.
   */
  status?: SectionStatus | null;
  defaultOpen?: boolean;
  /**
   * Whether a status change moves the card (outstanding opens, finished
   * collapses). Default true — the session page's steps are whole actions
   * elsewhere. The device inspection passes false: its sections finish under
   * the operator's thumb, and collapsing on the last Yes/No would shut the
   * card while they are still adding that row's note. With false, the status
   * only decides how the card starts.
   */
  followStatus?: boolean;
  /**
   * If set, the section reflects this value on every render rather than
   * tracking its own open state. Useful when the parent wants a section
   * forced-open (e.g. audit auto-opens on requiresAttention).
   */
  forceOpen?: boolean;
  onToggle?: (open: boolean) => void;
  children: React.ReactNode;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  adornment,
  status,
  defaultOpen = true,
  followStatus = true,
  forceOpen,
  onToggle,
  children,
}) => {
  const [internalOpen, setInternalOpen] = useState(status ? status === 'todo' : defaultOpen);
  // Once the operator has opened or closed this card themselves, the status no
  // longer moves it. Collapsing a panel someone just opened — which is what
  // marking a step done would otherwise do the moment Mark Paid lands — is a
  // worse failure than leaving a finished panel open.
  const operatorToggled = useRef(false);
  const open = forceOpen ?? internalOpen;

  useEffect(() => {
    if (!status || !followStatus || operatorToggled.current) return;
    setInternalOpen(status === 'todo');
  }, [status, followStatus]);

  const handleToggle = () => {
    operatorToggled.current = true;
    if (forceOpen !== undefined) {
      // Parent controls; surface the click via the callback.
      onToggle?.(!open);
      return;
    }
    const next = !internalOpen;
    setInternalOpen(next);
    onToggle?.(next);
  };

  return (
    <div className={`checkin-card${status ? ` checkin-card--${status}` : ''}`}>
      <div
        onClick={handleToggle}
        className={`checkin-card__head${open ? ' checkin-card__head--open' : ''}`}
        role="button"
        tabIndex={0}
        aria-expanded={open}
        aria-current={status === 'todo' ? 'step' : undefined}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <span className="checkin-card__title">{title}</span>
        {status === 'done' && (
          <Chip tone="success">
            <IconCheck size={12} stroke={2} />
            Done
          </Chip>
        )}
        {/*
          Not a `Chip tone="warning"`: the outstanding card tints its own header
          the same #fffbf0 that a warning chip uses as a background, so the pill
          would dissolve into it. Solid amber is the one state that has to carry
          across the room.
        */}
        {status === 'todo' && (
          <span className="checkin-card__todo">
            <IconAlertCircle size={12} stroke={2} />
            To do
          </span>
        )}
        {adornment}
        <IconChevronDown
          className={`checkin-card__chev${open ? ' checkin-card__chev--open' : ''}`}
          size={18}
          stroke={1.5}
        />
      </div>
      <Collapse in={open} unmountOnExit>
        <div className="checkin-card__body">{children}</div>
      </Collapse>
    </div>
  );
};
